import styled from "styled-components";
import { NavLink } from "react-router-dom";

export const LayoutContainer = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

export const Sidebar = styled.aside<{ $collapsed: boolean }>`
  width: ${(props) => (props.$collapsed ? "80px" : "250px")};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  overflow: hidden;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

export const Logo = styled.div<{ $collapsed: boolean }>`
  font-size: ${(props) => (props.$collapsed ? "1.5rem" : "1.8rem")};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.TextWhite};
`;

export const CollapseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.TextWhite};
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

export const NavItems = styled.ul`
  list-style: none;
  padding: 8px;
  margin: ${({ theme }) => theme.spacing.md} 0;
  flex-grow: 1;
  overflow-y: auto;
`;

export const NavItem = styled.li`
  margin: 8px 0px 8px 0px;
  padding: 0;
  width:100%;
  @media (max-width: 768px) {
    margin: 0;
    padding: 0;
  }
`;

export const StyledNavLink = styled(NavLink)<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  color: ${({ theme }) => theme.colors.TextWhite};
  text-decoration: none;
  transition: all 0.2s ease;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  width: 100%;
  &:hover {
    background-color: ${({ theme }) => theme.colors.darkGraylight};
    text-decoration: none;
  }

  &.active {
    background-color: ${({ theme }) => theme.colors.secondary};
    text-decoration: none;
  }
`;

export const NavIcon = styled.span`
  margin-right: ${({ theme }) => theme.spacing.sm};
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
`;

export const NavLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  margin-top: 4px;
`;

export const LogoutButton = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.md}`};
  color: ${({ theme }) => theme.colors.TextWhite};
  fontsize: ${({ theme }) => theme.fontSizes.large};
  fontweight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  width: 100%;
  text-align: left;
`;

export const MainContent = styled.main<{ $collapsed: boolean }>`
  flex-grow: 1;
  margin-left: 0;
  width: ${(props) =>
    props.$collapsed ? "calc(100% - 80px)" : "calc(100% - 250px)"};
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.lightGray};
  box-shadow: 0 2px 4px #cccccc;
  height: auto;
  min-height: 65px;
`;

export const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.large};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

export const UserSection = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 630px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

export const NotificationIcon = styled.div`
  margin-right: ${({ theme }) => theme.spacing.md};
  font-size: 1.2rem;
  cursor: pointer;
`;

export const UserAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
`;

export const ContentArea = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  overflow-y: auto;
  flex-grow: 1;

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

export const OutletContainer = styled.div`
  max-width: 1920px;
  margin: 0 auto;
`;

export const VerificationBadge = styled.div<{ $isVerified: boolean }>`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ $isVerified, theme }) =>
    $isVerified ? "rgba(76, 175, 80, 0.1)" : "rgba(244, 67, 54, 0.1)"};
  color: ${({ $isVerified, theme }) =>
    $isVerified ? theme.colors.success : theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  svg {
    margin-right: ${({ theme }) => theme.spacing.sm};
    min-width: 16px;
  }
`;

export const VerificationText = styled.div<{ $isVerified: boolean }>`
  color: ${({ $isVerified, theme }) =>
    $isVerified ? theme.colors.success : theme.colors.lightGrayTitle};

  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;

  @media (max-width: 678px) {
    width: 100%;
  }
`;

export const InfoView = styled.span<{ $isVerified: boolean }>`
  color: ${({ $isVerified, theme }) =>
    $isVerified ? theme.colors.success : theme.colors.lightGrayTitle};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  cursor: pointer;
`;
